import { generatePath } from 'react-router-dom';

import type { FundCarouselItem } from '@/api/fund-views/types';
import { theme, Tooltip, Typography } from '@/design-system';
import { PagePaths } from '@/routes/page-paths';
import { EmptyState } from '@/shared/design-system';

import { FollowFundButton } from './follow-fund-button';
import {
  FollowingLogo,
  FollowingLogoFallback,
  FollowingRow,
  FollowingRowLink,
  FollowingRows,
  FollowingText,
} from './following-list.styled';
import { FollowingListSkeleton } from './following-list-skeleton';

const NAME_TOOLTIP_LENGTH = 20;

interface FollowingListProps {
  funds: FundCarouselItem[];
  isLoading?: boolean;
}

export const FollowingList = ({ funds, isLoading = false }: FollowingListProps) => {
  if (isLoading) {
    return <FollowingListSkeleton />;
  }

  if (funds.length === 0) {
    return <EmptyState variant="tertiary" title="You are not following any funds yet." />;
  }

  return (
    <FollowingRows>
      {funds.map((fund) => (
        <FollowingRow key={fund.fundId}>
          <FollowingRowLink to={generatePath(PagePaths.allFundsId, { identifier: fund.slug })}>
            {fund.logoUrl ? (
              <FollowingLogo src={fund.logoUrl} alt={`${fund.fundName} logo`} />
            ) : (
              <FollowingLogoFallback />
            )}

            <FollowingText>
              <Tooltip
                title={fund.fundName}
                side="top"
                disabled={fund.fundName.length <= NAME_TOOLTIP_LENGTH}
              >
                <Typography variant="body2" fontWeight={600} noWrap>
                  {fund.fundName}
                </Typography>
              </Tooltip>

              <Tooltip
                title={fund.entityName}
                side="top"
                disabled={(fund.entityName?.length ?? 0) <= NAME_TOOLTIP_LENGTH}
              >
                <Typography variant="body3" color={theme.colors.text.secondary} noWrap>
                  {fund.entityName}
                </Typography>
              </Tooltip>
            </FollowingText>
          </FollowingRowLink>

          <FollowFundButton fundId={fund.fundId} fundName={fund.fundName} isFollowed />
        </FollowingRow>
      ))}
    </FollowingRows>
  );
};
